import React, { useEffect, useState } from 'react';
import {
  Box, FormControl, InputLabel, MenuItem,
  Select, SelectChangeEvent, Typography, Card, CardContent, Button,
  Autocomplete, TextField
} from '@mui/material';
import { mlgraph } from '../../services/mlgraph-service';
import DataTable from '../../components/tables/DataTable';
import { Column } from '../../components/tables/interfaces';
import PrimaryLayout from '../../components/layout/PrimaryLayout';
import GraphCanvas from './GraphCanvas';

export default function MLGraphViewer() {
  const [clusters, setClusters] = useState<string[]>([]);
  const [selectedCluster, setSelectedCluster] = useState<string | null>(null);
  const [distribution, setDistribution] = useState<any[]>([]);
  const [clusterIds, setClusterIds] = useState<number[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [selectedSize, setSelectedSize] = useState<number | null>(null);
  const [selectedClusterId, setSelectedClusterId] = useState<number | null>(null);

  useEffect(() => {
    mlgraph.getClusters().then((res) => setClusters(res.clusters || []));
  }, []);

  useEffect(() => {
    if (!selectedCluster) return;

    mlgraph.getClustersClusterNameDistribution(selectedCluster).then((res) => {
      const raw = res.size_distribution as unknown as [number, number][];
      const computed = raw.map(([size, count]) => ({
        cluster_size: size,
        count,
        total_records: size * count
      }));

      const total = computed.reduce((sum, row) => sum + row.total_records, 0);

      const transformed = computed.map((row) => ({
        ...row,
        percentage: ((row.total_records / total) * 100).toFixed(1) + '%',
        actions: <Button size="small" variant="outlined">View</Button>
      }));

      setTotalRecords(total);
      setDistribution(transformed);
    });
  }, [selectedCluster]);

  const handleClusterChange = (event: any, value: string | null) => {
    if (value) {
      setSelectedCluster(value);
      setSelectedSize(null);
      setClusterIds([]);
    }
  };

  const handleRowClick = async (row: any) => {
    const size = row.cluster_size;
    setSelectedSize(size);

    if (selectedCluster && size) {
      const result = await mlgraph.getClusterIdsBySize(selectedCluster, size) as { cluster_ids?: number[] };
      console.log('🪲 getClusterIdsBySize →', result);
      if (result && Array.isArray(result.cluster_ids)) {
        setClusterIds(result.cluster_ids);
      } else {
        setClusterIds([]);
      }
    }
  };

  const handleClusterIdSelect = (event: SelectChangeEvent<number>) => {
    setSelectedClusterId(Number(event.target.value));
  };

  const columns: Column[] = [
    {
      id: 'cluster_size', label: 'Size', type: 'numeric',
      onCellClick: (value, row) => handleRowClick(row)
    },
    { id: 'count', label: '#Nodes', type: 'numeric' },
    { id: 'total_records', label: '#Total', type: 'numeric' },
    { id: 'percentage', label: 'Percentage', type: 'string' }
  ];

  const fetchData = async () => distribution;
  const dataExtractor = (rows: any[]) => rows;

  return (
    <PrimaryLayout>
      <Box sx={{ mx: 2, my: 1 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          MLGraph
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select a config, then view cluster sizes and breakdowns.
        </Typography>
      </Box>

      <Box display="flex" alignItems="center" sx={{ m: 2 }}>
        <Autocomplete
          options={clusters}
          value={selectedCluster}
          autoHighlight
          onChange={handleClusterChange}
          sx={{ width: '20%', mr: 2 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Config Name"
              variant="outlined"
              size="small"
            />
          )}
        />
      </Box>

      {selectedCluster && (
        <Box display="flex" gap={4} alignItems="flex-start" sx={{ m: 2 }}>
          <Box sx={{ width: '20%' }}>
            <DataTable
              columns={columns}
              fetchData={fetchData}
              dataExtractor={dataExtractor}
              stickyHeader
              tableContainerMaxHeight={'calc(100vh - 270px)'}
            />
          </Box>

          {clusterIds.length > 0 && (
            <Box>
              <FormControl fullWidth sx={{ minWidth: 200 }}>
                <InputLabel>Cluster ID (size: {selectedSize})</InputLabel>
                <Select
                  value={selectedClusterId ?? ''}
                  onChange={handleClusterIdSelect}
                  label="Cluster ID"
                >
                  {clusterIds.map((id) => (
                    <MenuItem key={id} value={id}>{id}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
      )}
      {selectedClusterId != null && (
        <Box sx={{ m: 2 }}>
          <GraphCanvas
            clusterName={selectedCluster!}
            clusterId={selectedClusterId}
          />
        </Box>
      )}
    </PrimaryLayout>
  );
}
